import { z } from "@/zod";
import {
  nonNegativeDecimalStringSchema,
  twoPlacesDecimalString,
  roleId,
  storeId,
  ulid,
  twoPlacesDecimalStringAllowZero,
} from "@/schemas/commonFields";
import { contractorVisit, VISIT_STATUS } from "./contractorVisit";
import { PROJECT_STATUS } from "./project";
import { roleBasedProjectRates } from "./roleBasedProjectRates";
import { scheduleFormWeekdaysSchema } from "./scheduleFormWeekdays";

export const readProjectScheduleVisit = z.object({
  date: z.string().date(),
  id: z.string().length(26),
  status: z.enum(VISIT_STATUS),
  kam_planned_duration: nonNegativeDecimalStringSchema,
  pr_planned_duration: nonNegativeDecimalStringSchema.nullable(),
});

const projectSchedule = z.object({
  id: z.string().length(26),
  store_id: storeId,
  store_ref_number: z.number(),
  store_name: z.string(),
  network: z.string().nullable(),
  city: z.string().nullable(),
  street_name: z.string().nullable(),
  store_format: z.string().nullable(),
  store_number: z.string().nullable(),
  store_status: z.string(),
  client_store_number: z.string().nullable(),
  project_id: ulid,
  project_ref_number: z.number(),
  project_name: z.string(),
  project_status: z.enum(PROJECT_STATUS),
  project_role_id: ulid,
  project_role_name: z.string(),
  store_coordinator_name: z.string().nullable(),
  supervisor_name: z.string().nullable(),
  regional_representative_name: z.string().nullable(),
  regional_director_name: z.string().nullable(),
  total_duration: nonNegativeDecimalStringSchema,
  settlement_type: z.string(),
});

export const readProjectSchedule = projectSchedule
  .extend({ visits: z.array(readProjectScheduleVisit) })
  .readonly();

export const displayProjectSchedule = projectSchedule.readonly();

export const writeProjectScheduleVisit = z.object({
  suggested_start_time: z.string().min(1),
  suggested_end_time: z.string().min(1),
  kam_planned_duration: twoPlacesDecimalStringAllowZero,
});

export const createProjectSchedule = z.object({
  store_id: storeId,
  project_role_id: roleId,
  hour_allocation_type: z.string(),
  duration: z.string(),
  suggested_start_time: z.string(),
  suggested_end_time: z.string(),
  rounding: z.boolean(),
  settlement_type: z.string(),
  dates: z.array(z.string().date()),
});

export const updateProjectSchedule = createProjectSchedule
  .omit({
    settlement_type: true,
  })
  .extend({
    id: ulid,
  });

const baseProjectScheduleForm = z.object({
  hour_allocation_type: z.string().min(1),
  settlement_type: z.string().min(1),
  kam_planned_duration: twoPlacesDecimalString,
  date_start: z.string(),
  date_end: z.string(),
  suggested_start_time: z.string(),
  suggested_end_time: z.string(),
  weekdays: scheduleFormWeekdaysSchema,
  rounding: z.boolean().optional(),
  every_second_week: z.boolean(),
});

export const startBeforeEndRefinement: Parameters<
  (typeof baseProjectScheduleForm)["refine"]
> = [
  (data) => data.date_end >= data.date_start,
  {
    message: "validation.date_end_cannot_be_before_start",
    path: ["date_end"],
  },
];

export const createProjectScheduleForm = baseProjectScheduleForm.refine(
  ...startBeforeEndRefinement,
);

export const updateProjectScheduleForm = baseProjectScheduleForm
  .omit({
    settlement_type: true,
  })
  .extend({
    settlement_type: z.string().optional(),
  })
  .refine(...startBeforeEndRefinement);

export const writeProjectSchedulesForm = baseProjectScheduleForm
  .extend({
    stores: z.record(storeId, z.boolean()),
    project_role_id: roleId,
  })
  .refine(...startBeforeEndRefinement);

export const writeProjectSchedulesFormResolver =
  writeProjectSchedulesForm.superRefine((data, ctx) => {
    // Validate stores has at least one selected
    const hasSelectedStore = Object.values(data.stores).some(
      (selected) => selected,
    );

    if (!hasSelectedStore) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "projectSchedules.errors.stores",
        path: ["stores"],
      });
    }
  });

export const writeProjectSchedulesFormWithoutStores = baseProjectScheduleForm
  .extend({
    project_role_id: roleId,
  })
  .refine(...startBeforeEndRefinement);

const projectScheduleContractorVisit = contractorVisit.extend({
  rates: roleBasedProjectRates,
});

export const readProjectScheduleContractorVisit =
  projectScheduleContractorVisit.readonly();
